import * as React from "react";
import { ActivityIndicator, Pressable, Text } from "react-native";
import { cva, VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";

export type ButtonProps = React.ComponentProps<typeof Pressable> &
  VariantProps<typeof buttonStyles> & {
    asChild?: boolean;
    componentBefore?: React.ReactNode;
    iconBefore?: React.ReactNode;
    iconAfter?: React.ReactNode;
    isLoading?: boolean;
    dataSet?: Record<string, string>;
  };

export const Button = React.forwardRef<
  React.ElementRef<typeof Pressable>,
  ButtonProps
>(function Button(props, forwardedRef) {
  const {
    className,
    iconBefore,
    iconAfter,
    componentBefore,
    variant,
    size,
    children,
    ...rest
  } = props;

  return (
    <Pressable
      role="button"
      className={cn(buttonStyles({ variant, size }), className)}
      {...rest}
      ref={forwardedRef}
      disabled={rest.disabled ?? rest.isLoading ?? false}
    >
      <>
        {rest.isLoading && <ActivityIndicator />}
        {componentBefore}
        {iconBefore && (
          <Text
            className={cn(labelStyles({ variant }), /*"text-[0px]"*/ undefined)}
          >
            {iconBefore}
          </Text>
        )}
        {typeof children === "string" ? (
          <Text className={labelStyles({ variant })}>{children}</Text>
        ) : (
          children
        )}
        {iconAfter && (
          <Text
            className={cn(labelStyles({ variant }), /*"text-[0px]"*/ undefined)}
          >
            {iconAfter}
          </Text>
        )}
      </>
    </Pressable>
  );
});

const buttonStyles = cva(
  "flex flex-row rounded-lg text-button-label gap-2 items-center justify-center border border-transparent disabled:opacity-80 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary: "bg-button-primary",
        secondary: "border border-button-secondary dark:bg-button-secondary",
        outline: "border border-input-border",
        ghost: "bg-transparent",
        danger: "bg-text-warning",
      },
      size: {
        large: "h-12 px-4",
        small: "px-2 h-9",
        tiny: "px-1 h-8",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "large",
    },
  },
);

const labelStyles = cva("text-base font-medium", {
  variants: {
    variant: {
      primary: "text-button-label",
      secondary: "text-primary dark:text-button-label",
      outline: "text-button-label",
      ghost: "text-button-label",
      danger: "text-text-primary",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});
